<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  inheritAttrs: false,
});
</script>
<!--
  https://raw.githubusercontent.com/wobsoriano/vuetify-sonner/refs/heads/main/lib/Toast.vue
  VuetifySonner didn't provide a close mechanism for the toast. It's a farily simple wrapper around
  vue-sonner, so we just use the vuetify-sonner code and modify it to include a decent close button
  modeled after current macOS notification close button (circular, slightly outside the upper left
  corner of notification)
-->
<script setup lang="ts">
import {
  VAvatar,
  VBtn,
  VCard,
  VCardActions,
  VCardText,
  VExpandTransition,
  VIcon,
  VImg,
  VProgressCircular,
  VSpacer,
} from 'vuetify/components';
import { ref } from 'vue';
import type { ToastProps } from './types';
import ProgressBar from './FlaglerToastProgressBar.vue';

;

const props = withDefaults(defineProps<ToastProps>(), {
  vertical: false,
  progressBar: false,
  progressDuration: 5000,
  cardActionsProps: () => ({}),
});

const emit = defineEmits(['closeToast']);

const isHovered = ref(false);
const wasRecentlyHovered = ref(false);
let timeout: ReturnType<typeof setTimeout> | undefined = undefined;

watch(isHovered, (newValue) => {
  if (newValue) {
    clearTimeout(timeout);
    timeout = undefined;
    wasRecentlyHovered.value = true;
  } else {
    timeout = setTimeout(() => {
      wasRecentlyHovered.value = false;
    }, 500);
  }
});

onBeforeUnmount(() => {
  if (timeout) {
    clearTimeout(timeout);
  }
});

function close() {
  emit('closeToast');
}

const cardClick = computed(() => {
  if (props.action) {
    return () => {
      emit('closeToast');
      props.action?.onClick?.();
    };
  } else {
    return undefined;
  }
});
</script>

<template>
  <VCard
    :class="{ 'card-snackbar': true, 'recently-hovered': wasRecentlyHovered }"
    v-bind="cardProps"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <VBtn
      v-if="closeButton"
      icon="tabler-x"
      density="compact"
      size="x-small"
      class="toast-close"
      :color="cardProps.color"
      @click="close"
    />
    <div
      :class="{
        'd-flex flex-no-wrap justify-space-between': !vertical,
        'cursor-pointer': !!action,
        'not-vertical': !vertical,
      }"
      @click="cardClick"
    >
      <VCardText
        v-bind="cardTextProps"
        :class="{
          'd-flex align-center': prependIcon || avatar || multipleAvatars,
        }"
      >
        <VAvatar v-if="avatar" class="mr-2" v-bind="avatarProps">
          <VImg :src="avatar" cover>
            <template #placeholder>
              <div class="d-flex align-center justify-center fill-height">
                <VProgressCircular color="grey-lighten-2" indeterminate />
              </div>
            </template>
          </VImg>
        </VAvatar>
        <div v-else-if="multipleAvatars" class="d-flex align-center mr-8">
          <VAvatar
            v-for="(img, i) in multipleAvatars.length <= 5
              ? multipleAvatars
              : multipleAvatars.slice(0, 5)"
            v-bind="avatarProps"
            :key="i"
            class="mr-n6"
            :style="{ zIndex: 5 - i }"
          >
            <VImg :src="img" cover>
              <template #placeholder>
                <div class="d-flex align-center justify-center fill-height">
                  <VProgressCircular color="grey-lighten-2" indeterminate />
                </div>
              </template>
            </VImg>
          </VAvatar>
        </div>
        <VIcon
          v-if="prependIcon"
          class="mr-2"
          :icon="prependIcon"
          v-bind="prependIconProps"
        />
        <div v-if="description">
          <div class="pb-1">
            {{ text }}
          </div>
          <p class="font-weight-light" v-html="sanitizeHtml(description)" />
        </div>
        <template v-else>
          {{ text }}
        </template>
      </VCardText>
      <VCardActions v-if="action" v-bind="cardActionsProps">
        <VSpacer />
        <VBtn
          v-bind="action.buttonProps"
          :text="action.label"
          @click="
            () => {
              $emit('closeToast');
              action?.onClick?.();
            }
          "
        />
      </VCardActions>
    </div>
    <VExpandTransition>
      <ProgressBar
        v-show="progressBar"
        :duration="progressDuration"
        :progress-bar-props="progressBarProps"
        :is-paused="isHovered"
        :reverse-progress-bar="reverseProgressBar"
      />
    </VExpandTransition>
  </VCard>
</template>

<style scoped lang="scss">
@use 'vuetify/settings';
.card-snackbar {
  background: settings.$snackbar-background;
  color: settings.$snackbar-color;
  max-width: 672px !important;
  min-width: 344px !important;
  min-height: 48px !important;
  overflow: visible;
  .v-btn.toast-close {
    opacity: 0;
    transition: opacity 0.33s ease-in-out;
    position: absolute;
    left: calc(0px - calc(var(--v-btn-height) / 5));
    top: calc(0px - calc(var(--v-btn-height) / 5));
    border: 1px solid currentColor;
  }
  &.recently-hovered .toast-close {
    opacity: 0.95;
  }
  div.not-vertical {
    .v-card-actions {
      align-items: flex-end;
    }
  }
}
</style>
